import type { CSSProperties } from 'vue'

import AudioElement from './AudioElement.vue'

import type { AudioElementInput, FlashcardSize } from '../types'
import type { ElementDefinition } from '../runtime/elementDefinition'
import { scaleWidth } from '../runtime/math'

const toFlag = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase()
    if (v === 'true' || v === '1' || v === 'yes' || v === 'on') return true
    if (v === 'false' || v === '0' || v === 'no' || v === 'off' || v === '') return false
  }
  return false
}

const estimateAudioBox = (input: AudioElementInput, size: FlashcardSize) => {
  const baseDiameter = typeof input.size === 'number' ? input.size : 44
  const diameterPx = scaleWidth(baseDiameter, size)
  if (!Number.isFinite(diameterPx) || diameterPx <= 0) return null
  return { widthPx: diameterPx, heightPx: diameterPx }
}

const definition: ElementDefinition<AudioElementInput> = {
  type: 'audio',
  component: AudioElement,
  estimateBox: (input, size) => estimateAudioBox(input, size),
  toProps: (input, ctx) => {
    if (!ctx.baseStyle || typeof input.src !== 'string') {
      return null
    }

    const diameterPx = scaleWidth(typeof input.size === 'number' ? input.size : 44, ctx.size)
    if (!Number.isFinite(diameterPx) || diameterPx <= 0) {
      return null
    }

    const iconSizePx = scaleWidth(
      typeof input.iconSize === 'number' ? input.iconSize : 14,
      ctx.size,
    )
    const safeIconSizePx = Number.isFinite(iconSizePx) && iconSizePx > 0 ? iconSizePx : 14

    const style: CSSProperties = {
      ...ctx.baseStyle,
      width: `${diameterPx}px`,
      height: `${diameterPx}px`,
      display: 'block',
      pointerEvents: 'auto',
    }

    return {
      src: input.src,
      autoplay: toFlag(input.autoplay),
      notVisible: toFlag(input.notVisible),
      loop: toFlag(input.loop),
      muted: toFlag(input.muted),
      preload: input.preload ?? 'metadata',
      style,
      iconSizePx: safeIconSizePx,
      face: ctx.context.face,
    }
  },
}

export default definition
